import { createSelector } from "@reduxjs/toolkit";
import memoize from "lodash/memoize";

import { selectActiveTask } from "Services/state/tasks/Selectors";

import { ConversationState as RootConversationsState } from "./IndexProvider";

export const selectRootConversationsState = (state: RootState): RootConversationsState => state.conversations;
export const selectActiveConversationsState = createSelector(
    selectRootConversationsState,
    (conversationsState) => conversationsState.activeConversations
);
export const selectUnreadConversationsState = createSelector(
    selectRootConversationsState,
    (conversationsState) => conversationsState.unreadConversations
);

/**
 * Active conversation SIDs derived from instances (single source of truth).
 */
export const selectActiveConversationSids = createSelector(selectActiveConversationsState, (activeConversations) =>
    Object.keys(activeConversations.instances)
);

/**
 * Parameterized selector (same pattern as selectPersistedThread): returns a memoized selector per sid
 * so that useSelector(selectConversationInstance)(sid) gives the Conversation instance from the store.
 */
export const selectConversationInstance = createSelector(selectActiveConversationsState, (activeConversations) =>
    memoize((sid: string | undefined) => (sid ? activeConversations.instances[sid] : undefined))
);

export const selectReservationMessages = createSelector(
    selectActiveConversationsState,
    (conversationsState) => conversationsState.reservationMessages
);

export const selectConversationMessages = createSelector(
    selectActiveConversationsState,
    (conversationsState) => conversationsState.conversationMessages
);

export const selectNotifiedMessages = createSelector(
    selectActiveConversationsState,
    (conversationsState) => conversationsState.notififiedMessages
);

export const selectNotYetNotifiedMessages = createSelector(
    selectConversationMessages,
    selectNotifiedMessages,
    (conversationMessages, notifiedMessages) => {
        const notNotifiedMessages: { [sid: string]: any[] } = {};
        for (const key in conversationMessages) {
            if (!notifiedMessages[key]) {
                notNotifiedMessages[key] = conversationMessages[key];
            } else {
                const notifiedMessageSids = notifiedMessages[key];
                const conversationMessagesSids = conversationMessages[key].map((message: any) => message.smsMessageSid);
                const notNotifiedMessageSids = conversationMessagesSids.filter(
                    (sid) => !notifiedMessageSids.includes(sid)
                );

                notNotifiedMessages[key] = conversationMessages[key].filter((message: any) =>
                    notNotifiedMessageSids.includes(message.smsMessageSid)
                );
            }
        }
        return notNotifiedMessages;
    }
);

export const selectTwilioConversationMessages = createSelector(
    selectActiveConversationSids,
    selectConversationMessages,
    selectReservationMessages,
    (sids, conversationMessages, reservationMessages) => {
        const messages: { [sid: string]: any[] } = {};

        for (let i = 0; i < sids.length; i++) {
            const sid = sids[i];
            messages[sid] = [];

            if (reservationMessages[sid]) {
                messages[sid].push(...reservationMessages[sid]);
            }

            if (conversationMessages[sid]) {
                messages[sid].push(...conversationMessages[sid]);
            }
        }
        return messages;
    }
);

export const selectActiveMessages = createSelector(
    selectTwilioConversationMessages,
    selectActiveTask,
    (twilioMessages, task) => {
        const activeSid = task?.attributes?.conversationSid;
        const messages = twilioMessages[activeSid] || [];
        return messages;
    }
);

/**
 * Unred Conversations
 */
export const selectUnreadSms = createSelector(
    selectUnreadConversationsState,
    (unreadConversationsState) => unreadConversationsState.sms
);
